import { theme } from '@agentisai/design-system';
import type { CSSProperties, MouseEventHandler, Ref } from 'react';
import { actions as actionsCls, deleteAction as deleteActionCls } from './WorkstreamCard.css';

export interface WorkstreamCardProps {
  readonly name: string;
  readonly description: string;
  readonly active?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Open the delete-confirm dialog for this workstream. When omitted the Delete
   * link isn't rendered (the card stays selection-only) — mirrors the DS
   * `FileCard`, where the destructive action lives at the bottom of the bubble.
   */
  readonly onDelete?: () => void;
  /** ref-as-prop (ADR-0014) → the card's selection `<button>`, so the rail can
   * scroll the selected card into view (S13). */
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * Single workstream bubble in the Workstreams rail panel (S-C3).
 *
 * Lives in the app (not the design system) per frontend-architecture R0 —
 * it's a page-composition component, not a reusable DS primitive. Mirrors
 * the styling of the DS `RailPanel` Workstreams story (`accent.teal` active
 * tint), with an extra non-colour indicator (a thick left border) so the
 * active state is perceivable without colour (WCAG 1.4.1). Active card
 * carries `aria-current="true"`.
 *
 * The bubble is a container `<div>` (the visual frame + active tint) holding
 * two siblings: the selection `<button>` (name + description) and — when
 * `onDelete` is set — an actions row with a Delete link, placed exactly like
 * the DS `FileCard`'s Remove link. Keeping them siblings avoids a button nested
 * inside a button (invalid + a broken a11y target).
 */
export function WorkstreamCard({
  name,
  description,
  active,
  onClick,
  onDelete,
  ref,
}: WorkstreamCardProps) {
  const borderColor = active ? theme.color.accent.teal[500] : theme.color.neutral.border;
  // Non-colour active indicator: a 3px left border vs. 1px elsewhere makes the
  // active state visually distinct in a black-and-white render too (WCAG 1.4.1).
  // The container is the frame ONLY (no padding) — the selection button + actions
  // row carry the padding, so the whole padded area is the selection target
  // rather than a dead container ring.
  const cardStyle: CSSProperties = {
    marginBottom: theme.space[2],
    background: active ? theme.color.accent.teal[50] : theme.color.neutral.white,
    border: `1px solid ${borderColor}`,
    borderLeftWidth: active ? '3px' : '1px',
    borderRadius: theme.radii.lg,
    fontFamily: theme.typography.fontFamily.sans,
  };
  // Inset the left padding by the extra 2px the active border adds so the text
  // doesn't shift between active / inactive; both the button and the actions row
  // share this so the Delete link's left edge aligns with the name.
  const padLeft = active ? `calc(${theme.space[3]} - 2px)` : theme.space[3];
  // The selection target fills the card and owns the padding + pointer; a smaller
  // bottom pad when a Delete link follows keeps the actions row close.
  const selectStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 0,
    paddingTop: theme.space[3],
    paddingRight: theme.space[3],
    paddingBottom: onDelete ? theme.space[2] : theme.space[3],
    paddingLeft: padLeft,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily.sans,
  };
  return (
    <div style={cardStyle}>
      <button
        ref={ref}
        type="button"
        aria-current={active ? 'true' : undefined}
        onClick={onClick}
        style={selectStyle}
      >
        <div
          style={{
            fontSize: theme.typography.fontSize.bodySm,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.color.neutral.body,
            marginBottom: theme.space[1],
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: theme.typography.fontSize.tiny,
            fontWeight: theme.typography.fontWeight.regular,
            color: theme.color.neutral.faint,
            lineHeight: theme.typography.lineHeight.body,
          }}
        >
          {description}
        </div>
      </button>
      {onDelete && (
        <div
          className={actionsCls}
          style={{
            paddingLeft: padLeft,
            paddingRight: theme.space[3],
            paddingBottom: theme.space[3],
          }}
        >
          <button
            type="button"
            className={deleteActionCls}
            onClick={onDelete}
            aria-label={`Delete ${name}`}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
WorkstreamCard.displayName = 'WorkstreamCard';
